import type { CSSProperties, ReactNode } from 'react';

export interface StyledBlockBoxDefaults {
  /** the gutter around the box */
  margin?: string;
  /** the box's own inner padding */
  padding?: string;
  background?: string;
  borderRadius?: string;
}

interface StyledBlockBoxProps {
  blockId: string;
  /** the user's overrides from the block's style panel - background, border, radius, padding and margin */
  style?: Record<string, unknown>;
  /** the template's own look, used for anything the user hasn't overridden */
  defaults?: StyledBlockBoxDefaults;
  /** where the box sits in its row */
  align?: 'left' | 'center' | 'right';
  /** the content's alignment inside the box - separate from align, since it inherits into nested tables */
  contentAlign?: 'left' | 'center' | 'right';
  /** shrink-wraps the box to its content instead of filling the row */
  fitContent?: boolean;
  /** a fixed-size box, e.g. chateau's countdown circle */
  width?: number;
  height?: number;
  children: ReactNode;
}

// wraps a template's hand-built section (an info card, a countdown badge) so the generic block style
// settings apply to it like they do to any other block: margin is the gutter, the rest style the box
export default function StyledBlockBox({
  blockId,
  style = {},
  defaults = {},
  align,
  contentAlign,
  fitContent = false,
  width,
  height,
  children,
}: StyledBlockBoxProps) {
  const borderStyle = style.borderStyle as string | undefined;
  const boxStyle: CSSProperties = {
    background: (style.background as string | undefined) ?? defaults.background,
    border:
      borderStyle && borderStyle !== 'none'
        ? `${(style.border as string | undefined) ?? '1px'} ${borderStyle} ${(style.borderColor as string | undefined) ?? 'currentColor'}`
        : undefined,
    borderRadius: (style.borderRadius as string | undefined) ?? defaults.borderRadius,
  };

  return (
    <table role="presentation" width="100%" cellPadding={0} cellSpacing={0} border={0}>
      <tbody>
        <tr>
          <td align={align} style={{ padding: (style.margin as string | undefined) ?? defaults.margin }}>
            <table
              data-block-id={blockId}
              width={fitContent ? width : '100%'}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={boxStyle}
            >
              <tbody>
                <tr>
                  <td
                    data-block-padding
                    align={contentAlign}
                    valign={height ? 'middle' : undefined}
                    width={width}
                    height={height}
                    style={{ padding: (style.padding as string | undefined) ?? defaults.padding }}
                  >
                    {children}
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
}
